import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Grid, TextField, useMediaQuery, useTheme } from '@mui/material';
import { TranslatableField as TranslatableFieldType } from 'common/types';
import { getDefault, getValueForLanguage, Languages } from 'common/modules/translations';
import { useTranslation } from 'services/localization';
import { getLanguageName } from 'common/locales/utils';
import { TranslationGroup } from 'reducers/TranslationsReducer';
import * as TranslationsSelectors from 'selectors/TranslationsSelectors';
import * as TranslationsActions from 'actions/TranslationsActions';

interface Props {
	docId: string;
	field: TranslatableFieldType;
	group: TranslationGroup;
}
const TranslatableField = React.memo((props: Props) => {
	const { t } = useTranslation();
	const dispatch = useDispatch();
	const activeLanguage = useSelector(TranslationsSelectors.activeLanguage);
	const defaultLanguage = useSelector(TranslationsSelectors.activeDefaultLanguage);
	const { docId, group, field } = props;
	const { id, label, value, textArea } = field;
	const theme = useTheme();
	const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));

	const handleValueChange = (language: Languages) => (
		e: React.ChangeEvent<HTMLTextAreaElement>,
	) => {
		dispatch(
			TranslationsActions.setFieldValue({
				group,
				docId,
				fieldId: id,
				value: e.target.value,
				language,
			}),
		);
	};

	const handleValueCommit = () => {
		if (!field.value?.def) return;
		dispatch(TranslationsActions.commitFieldValue({ fieldId: id }));
	};

	const defaultLabel = !isSmallScreen ? label : `${label} (${getLanguageName(defaultLanguage, t)})`;
	const translationLabel = !isSmallScreen
		? label
		: `${label} (${getLanguageName(activeLanguage, t)})`;

	const noDefaultValue = !value?.def;

	return (
		<Grid container spacing={2} sx={{ mt: 2 }} onBlur={handleValueCommit}>
			<Grid item xs={12} sm={6}>
				<TextField
					fullWidth
					value={getDefault(value)}
					onChange={handleValueChange(defaultLanguage)}
					variant="outlined"
					label={defaultLabel}
					multiline={textArea}
					minRows={textArea ? 3 : undefined}
					error={noDefaultValue}
					InputLabelProps={{
						shrink: true,
					}}
					helperText={noDefaultValue ? t('common:validation.cannotBeEmptyGeneric') : ''}
				/>
			</Grid>
			<Grid item xs={12} sm={6}>
				<TextField
					fullWidth
					variant="outlined"
					value={getValueForLanguage(value, activeLanguage)}
					onChange={handleValueChange(activeLanguage)}
					label={translationLabel}
					multiline={textArea}
					minRows={textArea ? 3 : undefined}
					InputLabelProps={{
						shrink: true,
					}}
				/>
			</Grid>
		</Grid>
	);
});

export default TranslatableField;
